import { useActiveToolSkeleton } from "@/hooks/useActiveToolSkeleton";
import { cn } from "@/lib/utils";
import {
  getOptimizedImageUrl,
  ImagePresets,
} from "@/lib/utils/supabaseImageTransform";
import equal from "fast-deep-equal";
import { memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AIErrorDisplay } from "./AIErrorDisplay";
import { StaticFileAttachments } from "./FileAttachments";
import ToolSkeletonLoader from "./ToolSkeletonLoader";

// Pure component to render file attachments independently
const PureFileAttachmentsRenderer = ({ messageId, files, isUser, open }) => {
  if (!files || files.length === 0) return null;

  return (
    <div
      className={cn(
        "transition-all duration-300 ease-in-out",
        isUser ? "flex justify-end" : "",
        "w-full max-w-4xl",
        "mb-1"
      )}
    >
      <div
        className={cn(
          "rounded-2xl overflow-hidden inline-block"
          // Remove background to prevent corner artifacts
        )}
      >
        <StaticFileAttachments
          files={files}
          messageId={`${messageId}-files`}
          key={`file-attachments-${messageId}`}
        />
      </div>
    </div>
  );
};

// Memoized file attachments renderer with deep equality comparison
export const FileAttachmentsRenderer = memo(
  PureFileAttachmentsRenderer,
  (prevProps, nextProps) => {
    if (prevProps.messageId !== nextProps.messageId) return false;
    if (prevProps.isUser !== nextProps.isUser) return false;
    if (prevProps.open !== nextProps.open) return false;
    return equal(prevProps.files, nextProps.files);
  }
);

FileAttachmentsRenderer.displayName = "FileAttachmentsRenderer";

// Helper to extract single image from various data structures (backward compat)
const extractSingleImage = (dalleImageData) => {
  let imageUrl = null;
  let imageName = "Generated Image";
  let imagePrompt = null;
  let imageProvider = null;
  let imageModel = null;
  let imageSize = null;
  let createdAt = null;

  if (typeof dalleImageData === "string") {
    imageUrl = dalleImageData;
  } else if (dalleImageData.result?.data?.url) {
    imageUrl = dalleImageData.result.data.url;
    imageName = dalleImageData.result.data.name || imageName;
    imagePrompt = dalleImageData.result.data.prompt;
    imageProvider = dalleImageData.result.metadata?.provider || dalleImageData.result.data.provider;
    imageModel = dalleImageData.result.metadata?.model || dalleImageData.result.data.model;
    imageSize = dalleImageData.result.metadata?.parameters?.size || dalleImageData.result.data.parameters?.size;
    createdAt = dalleImageData.result.metadata?.created_at || dalleImageData.result.data.created_at;
  } else if (dalleImageData.result?.result?.data?.url) {
    imageUrl = dalleImageData.result.result.data.url;
    imageName = dalleImageData.result.result.data.name || imageName;
    imagePrompt = dalleImageData.result.result.data.prompt;
    imageProvider = dalleImageData.result.result.metadata?.provider || dalleImageData.result.result.data.provider;
    imageModel = dalleImageData.result.result.metadata?.model || dalleImageData.result.result.data.model;
  } else if (dalleImageData.result?.url) {
    imageUrl = dalleImageData.result.url;
    imageName = dalleImageData.result.name || imageName;
    imagePrompt = dalleImageData.result.prompt;
    imageProvider = dalleImageData.metadata?.provider || dalleImageData.result.provider;
    imageModel = dalleImageData.metadata?.model || dalleImageData.result.model;
  } else if (dalleImageData.data?.url) {
    imageUrl = dalleImageData.data.url;
    imageName = dalleImageData.data.name || imageName;
    imagePrompt = dalleImageData.data.prompt;
    imageProvider = dalleImageData.metadata?.provider || dalleImageData.data.provider;
    imageModel = dalleImageData.metadata?.model || dalleImageData.data.model;
  } else if (dalleImageData.url) {
    imageUrl = dalleImageData.url;
    imageName = dalleImageData.name || imageName;
    imagePrompt = dalleImageData.prompt;
    imageProvider = dalleImageData.provider;
    imageModel = dalleImageData.model;
  }

  // Try to infer provider from URL if not explicitly set
  if (!imageProvider && imageUrl) {
    if (imageUrl.includes("fal.media") || imageUrl.includes("fal.ai")) {
      imageProvider = "fal";
      if (!imageModel) imageModel = "recraft-v3";
    } else if (imageUrl.includes("oaidalleapiprodscus.blob.core.windows.net")) {
      imageProvider = "openai";
      if (!imageModel) imageModel = "dall-e-3";
    } else if (imageUrl.includes("storage.googleapis.com")) {
      imageProvider = "google";
    }
  }

  if (!imageUrl) return null;

  return {
    url: imageUrl,
    name: imageName,
    prompt: imagePrompt,
    provider: imageProvider,
    model: imageModel,
    size: imageSize,
    createdAt,
  };
};

// Pure component to render DALL-E images independently (supports multi-image)
const PureDalleImageRenderer = ({
  dalleImageData,
  messageId,
  threadId,
  toolCallId,
  open,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (!dalleImageData) return null;

  // Check for error in the image generation result
  if (dalleImageData.success === false || dalleImageData.error) {
    return (
      <div className="w-full max-w-3xl mb-1">
        <AIErrorDisplay
          error={dalleImageData.error || "Image generation failed"}
          errorType={dalleImageData.errorType || "image_generation"}
        />
      </div>
    );
  }

  // Extract images - check for images array first (new multi-image format)
  let images = [];

  // Check various nested structures for images array
  if (dalleImageData.result?.images && Array.isArray(dalleImageData.result.images)) {
    images = dalleImageData.result.images;
  } else if (dalleImageData.result?.result?.images && Array.isArray(dalleImageData.result.result.images)) {
    images = dalleImageData.result.result.images;
  } else if (dalleImageData.images && Array.isArray(dalleImageData.images)) {
    images = dalleImageData.images;
  } else {
    // Fallback: existing single-image extraction for backward compatibility
    const singleImage = extractSingleImage(dalleImageData);
    if (singleImage) {
      images = [singleImage];
    }
  }

  if (images.length === 0) return null;

  // Generate short UUID-like ID from long ID (same hash function as library)
  const generateShortId = (longId) => {
    if (!longId) return "";
    let hash = 0;
    for (let i = 0; i < longId.length; i++) {
      const char = longId.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).padStart(8, "0").substring(0, 8);
  };

  const handleImageClick = (image, index) => {
    if (!image.url || !messageId) return;

    // Generate deterministic ID matching library page format
    const imageId = toolCallId
      ? `${messageId}-${toolCallId}-${index}`
      : `${messageId}-${index}`;
    const shortId = generateShortId(imageId);

    // Navigate with state for instant modal rendering
    navigate(`/library/image/${shortId}`, {
      state: {
        backgroundLocation: location,
        imageData: {
          id: imageId,
          url: image.url,
          thumbnailUrl: image.url,
          prompt: image.prompt || image.name,
          messageId: messageId,
          threadId: threadId,
          imageProvider: image.provider,
          imageModel: image.model,
          imageSize: image.size || image.parameters?.size,
          createdAt: image.createdAt || new Date().toISOString(),
          // Gallery context for navigation
          galleryIndex: index,
          galleryTotal: images.length,
        },
      },
    });
  };

  // Image size classes based on count - responsive for mobile
  const getImageClasses = (count) => {
    if (count === 1) {
      return "max-h-[600px] max-w-full sm:max-w-[500px]"; // Full width on mobile, constrained on larger screens
    }
    return "max-h-[300px] max-w-full sm:max-w-[250px]"; // Full width on mobile, smaller for multiple images on larger screens
  };

  return (
    <div
      className={cn(
        "transition-all duration-300 ease-in-out",
        "flex justify-start",
        "w-full max-w-3xl",
        "mb-1"
      )}
    >
      <div className={cn("flex flex-wrap gap-2 w-full", images.length > 1 && "sm:max-w-[520px]")}>
        {images.map((image, index) => {
          const imageId = toolCallId
            ? `${messageId}-${toolCallId}-${index}`
            : `${messageId}-${index}`;
          const shortId = generateShortId(imageId);

          return (
            <div
              key={shortId}
              className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => handleImageClick(image, index)}
              data-image-id={shortId}
            >
              <img
                src={getOptimizedImageUrl(image.url, ImagePresets.preview)}
                alt={image.prompt || image.name || `Generated Image ${index + 1}`}
                className={cn(
                  "w-auto h-auto block",
                  getImageClasses(images.length)
                )}
                loading="lazy"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Memoized DALL-E image renderer with selective comparison
export const DalleImageRenderer = memo(
  PureDalleImageRenderer,
  (prevProps, nextProps) => {
    if (prevProps.messageId !== nextProps.messageId) {
      return false;
    }
    if (prevProps.open !== nextProps.open) {
      return false;
    }

    const isEqual = equal(prevProps.dalleImageData, nextProps.dalleImageData);
    return isEqual;
  }
);

DalleImageRenderer.displayName = "DalleImageRenderer";

// Pure component to render charts independently
const PureChartRenderer = ({ chartData, messageId, open }) => {
  if (!chartData) return null;

  return (
    <div
      className={cn(
        "transition-all duration-300 ease-in-out",
        "flex justify-start",
        "w-full max-w-4xl",
        "mb-1"
      )}
    >
      <div className="px-3 sm:px-4 md:px-4">
        <h3 className="text-sm text-zinc-400 mb-2">
          {chartData.title || "Generated Chart"}
        </h3>
        {/* Chart renderer removed */}
      </div>
    </div>
  );
};

// Memoized chart renderer with deep equality comparison
export const ChartRenderer = memo(PureChartRenderer, (prevProps, nextProps) => {
  if (prevProps.messageId !== nextProps.messageId) {
    return false;
  }
  if (prevProps.open !== nextProps.open) {
    return false;
  }

  const isEqual = equal(prevProps.chartData, nextProps.chartData);
  return isEqual;
});

ChartRenderer.displayName = "ChartRenderer";

// Pure component to render sources skeleton during firecrawl search
const PureSourcesSkeletonRenderer = ({ messageId, open }) => {
  return (
    <div
      className={cn(
        "transition-all duration-300 ease-in-out",
        "flex justify-start",
        "w-full max-w-4xl",
        "mb-1"
      )}
    >
      <div className="px-3 sm:px-4 md:px-4">
        <ToolSkeletonLoader toolCategory="web_search" />
      </div>
    </div>
  );
};

// Memoized sources skeleton renderer
export const SourcesSkeletonRenderer = memo(
  PureSourcesSkeletonRenderer,
  (prevProps, nextProps) => {
    if (prevProps.messageId !== nextProps.messageId) return false;
    if (prevProps.open !== nextProps.open) return false;
    return true;
  }
);

SourcesSkeletonRenderer.displayName = "SourcesSkeletonRenderer";

/**
 * Unified streaming skeletons component
 * Uses useActiveToolSkeleton hook to reactively display tool-specific loading indicators
 * Shows on both mobile and desktop, even when canvas is open
 */
export const StreamingSkeletons = ({ isLastMessage, isUserMessage }) => {
  const { toolCategory, isActive, displayMessage } = useActiveToolSkeleton();

  // Only show for last message, non-user messages, when a tool is active
  if (isUserMessage || !isLastMessage || !isActive) {
    return null;
  }

  return (
    <div
      className={cn(
        "transition-all duration-300 ease-in-out",
        "flex justify-start",
        "w-full max-w-4xl",
        "mb-1"
      )}
    >
      <div className="px-3 sm:px-4 md:px-4">
        <ToolSkeletonLoader toolCategory={toolCategory} message={displayMessage} />
      </div>
    </div>
  );
};
